/**
 * PageRenderer — renders a `page` object (Phase 4-6 Stage A).
 *
 * Mirrors HtmlViewerModule's iframe rendering but reads `attributes.html`
 * and `attributes.publish` from an object instead of `module.config.*`.
 * Keep HtmlViewerModule for legacy module-sourced pages until Stage B.
 *
 * Orchestrator role: this component OWNS the shared ref set (iframeRef,
 * baselineHtmlRef, renderedHtmlRef, iframeReadyRef, patchNonceRef,
 * pendingSectionFramesRef, pendingPatchHtmlRef) and the render-pipeline spine
 * (safetyStyles / buildInjected / postSectionFrame / drainSectionFrameQueue /
 * the computePatches decision effect / onLoad). The five extracted concerns —
 * live-stream ingestion, inspector/section selection, and publish/share/OG —
 * receive those refs + setForceFullReload + drainSectionFrameQueue ONE-WAY as
 * params; they never re-create a ref or import back into this module.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/features/auth/hooks/useSession';
import { pageIdsEqual, rawPageId } from '@/lib/page-id';
import { getInspectorScript } from '@/lib/inspector-script';
import { computePatches, type PatchMessage } from '@/lib/page-patch';
import { markSectionEntering } from './utils/page-renderer-helpers';
import type { PendingSectionFrame, PagePublishState } from './types';
import { usePageLiveStream } from './hooks/usePageLiveStream';
import { usePageInspector } from './hooks/usePageInspector';
import { usePageOgPublish } from './hooks/usePageOgPublish';
import PageStatusBar from './components/PageStatusBar';
import PageEmptyState from './components/PageEmptyState';
import PagePreviewModal from './PagePreviewModal';
import './report/report.css';

interface PageRendererProps {
  object: any;
  conversationId?: string;
}

// Per-conversation preference for inspector default state lives in
// ./lib/inspector-preference. Re-exported here for existing importers.
export {
  INSPECTOR_PREF_KEY_PREFIX,
  readInspectorPreference,
  writeInspectorPreference,
} from './lib/inspector-preference';

export default function PageRenderer({ object, conversationId }: PageRendererProps) {
  const attributes = (object?.attributes as Record<string, any>) || {};
  // The API mapper (mapObject) spreads entity attributes at the TOP LEVEL of
  // the response, so html/legacyModuleId/publish live on the object root, not
  // under .attributes. Fall back to .attributes.* defensively in case a
  // future caller decides to keep attributes nested.
  //
  // CRITICAL — list-vs-detail shape: the /pages list endpoint truncates html
  // to 8192 bytes (PAGE_LIST_HTML_PREVIEW_BYTES) so list payloads stay small.
  // Rendering that into the iframe gives the user a half-page with the
  // background visible but no content (head fits, body gets cut). We treat
  // the html as renderable only when </html> appears anywhere in it — the
  // closing tag is the unambiguous "I have the whole document" signal. A
  // tail-of-string check (e.g. /<\/html>\s*$/) was too strict: any
  // post-closing-tag whitespace, comment, or trailing newline would
  // false-positive as truncated and trigger an unnecessary re-fetch.
  const rawHtml: string | undefined = (object as any)?.html ?? attributes.html;
  const html: string | undefined =
    rawHtml && /<\/html>/i.test(rawHtml) ? rawHtml : undefined;
  const title: string | undefined =
    (object as any)?.title || object?.title || object?.name || attributes.title;
  const publish: PagePublishState | undefined = (object as any)?.publish ?? attributes.publish;

  // Live streaming state — reuse the same shared live-state map as
  // HtmlViewerModule. For object-sourced pages, the streaming event may key
  // by the page object id (preferred) or by a legacy module id — try both.
  // legacyModuleId is set by the page-object writer to the html_viewer
  // module id that useChat writes to during streaming, so without this read
  // the live-cache lookup misses and the just-streamed HTML is invisible.
  //
  // THIRD family key: the raw module id implied by the `pg_<moduleId>`
  // mirror convention. The live-state map is written under the RAW module id
  // (useChat's activeHtmlModules key) and list-shape objects can arrive
  // without legacyModuleId — without this derived key the mount-time seed
  // misses the in-flight build entirely and the "Building your page…"
  // skeleton never exits. (#pages-streaming-regression)
  const liveKey: string = object?.id;
  const legacyModuleId: string | undefined =
    (object as any)?.legacyModuleId ?? attributes.legacyModuleId;
  const derivedRawModuleId: string | undefined =
    typeof liveKey === 'string' && liveKey.startsWith('pg_') ? rawPageId(liveKey) : undefined;
  const liveSeedKeys = [liveKey, legacyModuleId, derivedRawModuleId];

  // Accept a live-event id in ANY family form (canonical pg_*, raw module
  // id, o:/m: composite) — the dispatch side canonicalises but the family
  // forms have historically leaked through, and a strict gate here silently
  // drops chunks mid-build.
  const matchesLiveId = useCallback(
    (incoming: unknown): boolean =>
      typeof incoming === 'string' &&
      incoming.length > 0 &&
      (pageIdsEqual(incoming, liveKey) ||
        (legacyModuleId ? pageIdsEqual(incoming, legacyModuleId) : false)),
    [liveKey, legacyModuleId],
  );

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Per-render nonce: parent embeds this into the inspector script at inject
  // time and stamps it into every patch postMessage. The iframe-side listener
  // rejects patch messages whose `n` field does not match. Because the
  // sandbox is `allow-scripts` without `allow-same-origin`, the iframe
  // cannot read its own parent origin — a shared nonce is the most
  // reliable authentication channel available.
  //
  // Rotation policy: the nonce is regenerated at every full srcDoc reassign
  // (`mustFullReload` branch and the onLoad-driven full-reload escalation).
  // Each srcDoc produces a brand-new document with a fresh security context,
  // so any previously leaked nonce is invalidated on reload.
  const generatePatchNonce = (): string =>
    typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function'
      ? (crypto as any).randomUUID()
      : 'n-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  const patchNonceRef = useRef<string>('');
  if (!patchNonceRef.current) {
    patchNonceRef.current = generatePatchNonce();
  }

  // Tracks the HTML currently rendered inside the iframe (either via srcDoc
  // on mount or via patch messages since). This is our diff baseline.
  // Separate from React state so it updates synchronously during a render
  // that chose the patch path — without triggering another commit.
  const renderedHtmlRef = useRef<string | null>(null);

  // True between the moment we assigned `srcDoc` and the iframe's `load`
  // firing. Patch messages posted during this window would race the
  // iframe's initial parse and may be delivered to the OLD document; defer
  // patches until the iframe is ready and then apply the latest diff.
  const iframeReadyRef = useRef<boolean>(false);
  const pendingPatchHtmlRef = useRef<string | null>(null);
  // SectionFrame protocol: section-update events arrive during the iframe's
  // initial parse window (between srcDoc assign and onLoad). We queue them
  // here and flush on iframe load. After the iframe is ready, new frames
  // bypass this queue and post directly.
  const pendingSectionFramesRef = useRef<PendingSectionFrame[]>([]);
  // Indirection ref so the `onHtmlUpdate` listener (in usePageLiveStream)
  // can drain via the latest `postSectionFrame` callback without
  // re-subscribing on every render.
  const postSectionFrameRef = useRef<((frame: PendingSectionFrame) => void) | null>(null);

  // Track the html string that the srcDoc baseline represents (unwrapped,
  // no safety styles). Used as the diff baseline for subsequent updates.
  const baselineHtmlRef = useRef<string | null>(null);

  // One-shot force-srcDoc flag. Set when a patch failed (origin mismatch,
  // section-not-found, DOM exception) and we need to re-render from scratch
  // for the next update. Cleared after the next srcDoc assignment.
  const [forceFullReload, setForceFullReload] = useState<number>(0);

  // Drain a queued list of section frames one-per-rAF so the browser gets
  // a chance to paint between each postMessage. Without staggering, a queue
  // of 5-10 frames (built up while the iframe parsed the 40-60s shell)
  // would post all patches in a single microtask — the iframe's inspector
  // script applies every DOM mutation before the first reflow, and the
  // user sees every section appear simultaneously instead of streaming.
  // rAF spacing means one patch per frame (~16ms) so the reveal is
  // visibly sequential without adding perceptible total latency.
  const drainSectionFrameQueue = useCallback((frames: PendingSectionFrame[]) => {
    if (frames.length === 0) return;
    let i = 0;
    const step = () => {
      if (i >= frames.length) return;
      postSectionFrameRef.current?.(frames[i]);
      i += 1;
      if (i < frames.length) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  const { token } = useSession();

  // ----------------------------------------------------------------- hooks --
  // Live-stream ingestion. Owns liveHtml/streamFinished/rebuildOverlay +
  // scheduleLiveHtml; threaded the orchestrator-owned refs + drain callback.
  const { liveHtml, rebuildOverlay } = usePageLiveStream({
    liveKey,
    legacyModuleId,
    liveSeedKeys,
    matchesLiveId,
    html,
    objectId: object?.id,
    token,
    baselineHtmlRef,
    renderedHtmlRef,
    iframeReadyRef,
    pendingSectionFramesRef,
    drainSectionFrameQueue,
    setForceFullReload,
  });

  // When the object id changes, invalidate the patch baseline and mark the
  // iframe as not-ready so the next srcDoc assignment starts fresh. Lives in
  // the orchestrator because it touches the shared, orchestrator-owned refs.
  useEffect(() => {
    renderedHtmlRef.current = null;
    iframeReadyRef.current = false;
    pendingPatchHtmlRef.current = null;
    pendingSectionFramesRef.current = [];
  }, [liveKey]);

  // Inspector / section-selection. Owns inspectMode + autoActivateOnLoadRef.
  const { inspectMode, toggleInspect, autoActivateOnLoadRef } = usePageInspector({
    iframeRef,
    renderedHtmlRef,
    patchNonceRef,
    conversationId,
    objectId: object?.id,
    liveKey,
    setForceFullReload,
  });

  // Hoisted above the publish helpers so the publish flow can capture the
  // current draft html for OG-image rendering. The render path further
  // down references the same expression — keeping a single declaration
  // means the publish flow and the iframe never disagree on which html
  // is "current".
  const effectiveHtml = liveHtml || html;

  const [previewOpen, setPreviewOpen] = useState(false);

  // Publish / share / OG-capture. Self-contained: owns its own publish state
  // + effects; returns handlers + status the status bar renders.
  const {
    publishing,
    publicUrl,
    copied,
    error,
    publishStatus,
    draftHtmlHash,
    canNativeShare,
    handlePublish,
    handleUnpublish,
    handleCopy,
    handleNativeShare,
    handleDownload,
  } = usePageOgPublish({
    object,
    token,
    html,
    effectiveHtml,
    title,
    publish,
    matchesLiveId,
  });

  // ------------------------------------------------------- render pipeline --
  // Same safety CSS injection as HtmlViewerModule — prevents 100vh feedback
  // loops and forces responsive images inside the sandboxed iframe.
  // Memoized: these strings are stable for the lifetime of the component and
  // were previously rebuilt on every streaming chunk (~300ms). Hoisting them
  // into `useMemo` avoids the re-allocations during a streaming burst.
  const safetyStyles = useMemo(() => `<style>
html, body { margin: 0; padding: 0; min-height: 100%; }
img, video { max-width: 100%; height: auto; display: block; }
.hero, .Hero, .HERO,
header.hero, section.hero,
[class*="hero" i], [class*="banner" i], [class*="jumbotron" i],
[class*="masthead" i], [class*="splash" i], [class*="cover" i],
[class*="intro" i],
header:first-of-type, section:first-of-type,
body > div:first-child > section:first-of-type,
body > div:first-child > header:first-of-type {
  max-height: clamp(420px, 80vh, 760px);
}
body > div:first-child[class*="hero" i],
body > div:first-child[class*="banner" i],
body > div:first-child[class*="jumbotron" i] {
  max-height: clamp(420px, 80vh, 760px);
}
/* SectionFrame entering-animation. The parent stamps
 * data-xavior-stream-enter on a new sections root tag before posting it
 * to the iframe; the inspector script removes the attribute on the next rAF
 * after insertion, which triggers this transition. Reduced-motion
 * preference disables it. */
body [data-xavior-stream-enter] {
  opacity: 0;
  transform: translateY(12px);
}
body [data-section-id] {
  transition: opacity 180ms ease, transform 180ms ease;
}
@media (prefers-reduced-motion: reduce) {
  body [data-section-id] { transition: none !important; }
}
</style>`, []);
  // Build-and-inject helper: always reads the CURRENT `patchNonceRef.current`
  // so callers that just rotated the nonce pick up the new value. We can't
  // memoize the inspector script on `[]` deps (nonce rotation wouldn't
  // propagate) and we can't memoize it on the nonce ref (React doesn't track
  // ref changes). Call sites build on demand at full-reload time only —
  // patches go out via the in-iframe listener, not srcDoc.
  const buildInjected = useCallback(
    () => safetyStyles + getInspectorScript(patchNonceRef.current),
    [safetyStyles],
  );

  // srcDoc baseline — what we hand to the iframe's srcDoc attribute. Only
  // changes on (a) first render, (b) object switch, (c) a patch failure
  // that forced a full reload, or (d) a document-chrome change (head/html
  // differs) that computePatches flagged as full-reload.
  //
  // When the agent streams a section edit, `effectiveHtml` changes but
  // `srcDocHtml` should NOT — we'll dispatch a patch postMessage instead
  // and let the iframe stay in place.
  const [srcDocHtml, setSrcDocHtml] = useState<string | undefined>(undefined);

  // SectionFrame post-to-iframe helper. If the sectionId already exists in
  // the current DOM → patch (update in place). Otherwise → insert at the
  // append root (append to body or the marked `[data-xavior-page-root]`).
  // Either way, stamp the patch nonce and update the diff baseline so
  // subsequent legacy cumulative updates start from the new document state.
  const postSectionFrame = useCallback((frame: PendingSectionFrame) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    // Empty html on a frame targeting an existing sectionId is a remove
    // signal (emitted by useChat.ts for html.remove edits). Post
    // xavior-remove-section instead of an empty patch so the inspector
    // script tears down the section rather than replacing it with nothing.
    const isRemove = !frame.html || frame.html.trim() === '';
    if (isRemove && frame.sectionId) {
      win.postMessage({
        type: 'xavior-remove-section',
        sectionId: frame.sectionId,
        n: patchNonceRef.current,
      }, '*');
      if (frame.fullHtml) {
        baselineHtmlRef.current = frame.fullHtml;
        renderedHtmlRef.current = frame.fullHtml;
      }
      return;
    }
    const marked = markSectionEntering(frame.html);
    // Look in the current rendered doc (via renderedHtmlRef which tracks the
    // last-known iframe HTML). If the sectionId is present, this is an
    // update; otherwise an insert. We use a cheap regex rather than parsing
    // because we just need a boolean.
    const renderedHtml = renderedHtmlRef.current || baselineHtmlRef.current || '';
    const idRegex = new RegExp(
      `data-section-id\\s*=\\s*["']${frame.sectionId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`,
      'i',
    );
    const isUpdate = idRegex.test(renderedHtml);
    if (isUpdate) {
      win.postMessage({
        type: 'xavior-patch-section',
        sectionId: frame.sectionId,
        outerHTML: marked,
        n: patchNonceRef.current,
      }, '*');
    } else {
      win.postMessage({
        type: 'xavior-insert-section',
        beforeSectionId: null,
        sectionId: frame.sectionId,
        outerHTML: marked,
        n: patchNonceRef.current,
      }, '*');
    }
    if (frame.fullHtml) {
      baselineHtmlRef.current = frame.fullHtml;
      renderedHtmlRef.current = frame.fullHtml;
    }
  }, []);

  // Keep the indirection ref (used by drainSectionFrameQueue which closes
  // over postSectionFrameRef) pointing at the current postSectionFrame.
  useEffect(() => {
    postSectionFrameRef.current = postSectionFrame;
  }, [postSectionFrame]);

  // SectionFrame listener. Queues frames during iframe load; otherwise posts
  // immediately.
  useEffect(() => {
    function onSectionUpdate(e: Event) {
      const detail = (e as CustomEvent).detail as {
        moduleId: string;
        sectionId: string;
        html: string;
        fullHtml?: string;
      };
      // Defensive normalisation: strip the `o:` / `m:` composite prefix
      // PagesContainerModule attaches to its merged-list entries. A bug
      // in `toCanonicalPageId` previously let prefixed ids reach us and
      // every edit was silently dropped at the strict `!== liveKey`
      // check below — see #2719 follow-up. Even with the upstream fix
      // in place, accepting the legacy form here costs nothing and
      // guards future regressions.
      const incomingId = (detail.moduleId || '').startsWith('o:') || (detail.moduleId || '').startsWith('m:')
        ? detail.moduleId.slice(2)
        : detail.moduleId;
      if (incomingId && !incomingId.startsWith('pg_')) {
        console.warn(
          '[PageRenderer] xavior:html-section-update received non-canonical moduleId',
          {
            eventModuleId: detail.moduleId,
            normalisedId: incomingId,
            ourLiveKey: liveKey,
            ourLegacyId: legacyModuleId,
            sectionId: detail.sectionId,
          },
        );
      }
      // Family-match against the canonical pageId. legacyModuleId and the
      // raw module id implied by the `pg_<moduleId>` mirror convention
      // remain accepted as transitional fallbacks so chunks that key on the
      // pre-page-object module id still apply — without them, every edit
      // (and every live build chunk) during the migration window was lost
      // silently and the build skeleton never exited.
      //
      // SUNSET — re-tighten this gate to canonical-only (per the original
      // #1919 design) once one of these is true, whichever comes first:
      //   (a) the `[PageRenderer] xavior:html-section-update received
      //       non-canonical moduleId` warn above has been absent from
      //       production logs for 30 consecutive days, OR
      //   (b) all `html_viewer` modules with `legacyModuleId` attributes
      //       have been migrated (verify via persistence query — should
      //       complete during the page-object backfill).
      // The defensive `o:`/`m:` strip can stay either way; it's cheap
      // and self-documenting.
      if (!matchesLiveId(incomingId)) {
        window.dispatchEvent(new CustomEvent('xavior:patch-failed', {
          detail: {
            moduleId: detail.moduleId,
            sectionId: detail.sectionId,
            reason: 'moduleId-mismatch',
            liveKey,
            legacyModuleId,
          },
        }));
        return;
      }
      const frame: PendingSectionFrame = {
        sectionId: detail.sectionId,
        html: detail.html,
        fullHtml: detail.fullHtml,
      };
      if (!iframeReadyRef.current) {
        pendingSectionFramesRef.current.push(frame);
        return;
      }
      postSectionFrame(frame);
    }
    window.addEventListener('xavior:html-section-update', onSectionUpdate as EventListener);
    return () => window.removeEventListener('xavior:html-section-update', onSectionUpdate as EventListener);
  }, [liveKey, legacyModuleId, matchesLiveId, postSectionFrame]);

  // Decide each render: should we reassign srcDoc, or post patch messages?
  useEffect(() => {
    if (!effectiveHtml) {
      if (srcDocHtml !== undefined) {
        setSrcDocHtml(undefined);
        baselineHtmlRef.current = null;
        renderedHtmlRef.current = null;
        iframeReadyRef.current = false;
      }
      return;
    }

    // First render (no baseline yet) OR forced full reload: use srcDoc.
    // The "different-document" case (e.g. the html.init swapping in a brand
    // new build) is already handled below by checking whether computePatches
    // returned an `xavior-full-reload` directive — no need for a duplicate
    // head-comparison heuristic here.
    const needsFullReload =
      baselineHtmlRef.current === null || forceFullReload > 0;

    const tryPatch = (!needsFullReload && baselineHtmlRef.current)
      ? computePatches(baselineHtmlRef.current, effectiveHtml)
      : null;

    const mustFullReload = !tryPatch || tryPatch.some(p => p.type === 'xavior-full-reload');

    if (mustFullReload) {
      // Rotate the patch nonce: the reload produces a fresh document with a
      // fresh security context, so any previously leaked nonce must not
      // remain accepted. The freshly regenerated script embeds the new
      // nonce; subsequent patch postMessages use it via `patchNonceRef`.
      patchNonceRef.current = generatePatchNonce();
      const freshInjected = buildInjected();
      const wrapWithSafety = (body: string): string => {
        if (body.includes('<head>')) return body.replace('<head>', `<head>${freshInjected}`);
        if (body.includes('</head>')) return body.replace('</head>', `${freshInjected}</head>`);
        return `${freshInjected}${body}`;
      };
      setSrcDocHtml(wrapWithSafety(effectiveHtml));
      baselineHtmlRef.current = effectiveHtml;
      renderedHtmlRef.current = effectiveHtml;
      iframeReadyRef.current = false;
      if (forceFullReload > 0) setForceFullReload(0);
      return;
    }

    // Patch path — do not reassign srcDoc. Post messages to the iframe.
    const postPatches = (messages: PatchMessage[]) => {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      for (const m of messages) {
        if (m.type === 'xavior-full-reload') continue; // handled above
        // Targeted postMessage: the sandbox strips 'null' origin from the
        // parent's perspective, so we use '*' and rely on the nonce that
        // the inspector script checks before applying.
        win.postMessage({ ...m, n: patchNonceRef.current }, '*');
      }
    };

    if (iframeReadyRef.current) {
      postPatches(tryPatch);
      baselineHtmlRef.current = effectiveHtml;
      renderedHtmlRef.current = effectiveHtml;
    } else {
      // The iframe hasn't fired `load` yet — its srcDoc parse is still in
      // flight. Defer the patch by stashing the latest desired html; the
      // onLoad handler will compute a fresh diff and apply.
      pendingPatchHtmlRef.current = effectiveHtml;
    }
    // `srcDocHtml` intentionally excluded from deps — this effect SETS it
    // on the full-reload branch and reading it back would retrigger the
    // same cycle. `forceFullReload` is treated as a one-shot trigger and
    // reset inside the branch that consumed it.
  }, [effectiveHtml, buildInjected, forceFullReload]);

  if (!effectiveHtml) {
    return <PageEmptyState object={object} />;
  }

  return (
    <div
      className="module"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        minHeight: 0,
      }}
    >
      {effectiveHtml && (
        <PageStatusBar
          publish={publish}
          publicUrl={publicUrl}
          draftHtmlHash={draftHtmlHash}
          error={error}
          publishStatus={publishStatus}
          publishing={publishing}
          copied={copied}
          inspectMode={inspectMode}
          canNativeShare={canNativeShare}
          onToggleInspect={toggleInspect}
          onDownload={handleDownload}
          onCopy={handleCopy}
          onNativeShare={handleNativeShare}
          onPreview={() => setPreviewOpen(true)}
          onPublish={handlePublish}
          onUnpublish={handleUnpublish}
        />
      )}
      {previewOpen && effectiveHtml && (
        <PagePreviewModal
          html={effectiveHtml}
          title={title}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {/* Previously rendered an inline-styled badge strip here listing the
          selected sections. Removed — the canonical chip-row now lives in
          the chat composer (FocusChipRow) above the input, so users see one
          authoritative place for focused sections. `selectedSections` state
          is still maintained (in usePageInspector) for the iframe-side
          inspector highlights and fires `xavior:edit-sections-changed`
          events the chat composer listens to. */}
      <div
        className="html-viewer-iframe-wrap"
        style={{
          padding: 0,
          flex: 1,
          minHeight: 0,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {rebuildOverlay && (
          <div
            data-testid="page-renderer-rebuild-overlay"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: "'DM Sans', sans-serif",
              color: 'var(--color-text-muted, #6b7280)',
              fontSize: 13,
              zIndex: 2,
              pointerEvents: 'none',
            }}
            aria-live="polite"
          >
            Rebuilding page…
          </div>
        )}
        <iframe
          ref={iframeRef}
          title={title || 'Page'}
          sandbox="allow-scripts"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: 'var(--radius-md, 8px)',
            background: 'var(--color-surface)',
            display: 'block',
          }}
          srcDoc={srcDocHtml}
          onLoad={() => {
            // Mark iframe ready and apply any patches that were queued while
            // the initial srcDoc parse was in flight. The queued state is
            // always a newer full HTML — diff from the rendered baseline.
            iframeReadyRef.current = true;
            // Inspector-on-by-default: if the preference (per-conversation)
            // says inspect should be active on this render, send the
            // activate message now that the iframe has parsed. Done here
            // rather than in the effect so we never race the parse.
            if (autoActivateOnLoadRef.current) {
              autoActivateOnLoadRef.current = false;
              iframeRef.current?.contentWindow?.postMessage('xavior-inspect-on', '*');
            }
            // SectionFrame drain: replay any section frames that were queued
            // while the iframe was parsing, in receipt order. Runs BEFORE the
            // legacy computePatches fallback so the explicit section-frame
            // protocol wins over DOM-diff when both are in play.
            //
            // Stagger the drain via requestAnimationFrame — posting all
            // queued frames synchronously makes the iframe apply every
            // patch before the next paint, and the user sees every queued
            // section appear simultaneously instead of streaming.
            const queuedFrames = pendingSectionFramesRef.current;
            pendingSectionFramesRef.current = [];
            drainSectionFrameQueue(queuedFrames);
            // Legacy fallback: computePatches handles edit paths and any
            // stream that arrived as a cumulative HTML update (no section
            // frame was dispatched). Keep this path for backwards
            // compatibility with the DOM-diff renderer.
            const pending = pendingPatchHtmlRef.current;
            pendingPatchHtmlRef.current = null;
            if (pending && baselineHtmlRef.current && pending !== baselineHtmlRef.current) {
              const patches = computePatches(baselineHtmlRef.current, pending);
              if (patches.some(p => p.type === 'xavior-full-reload')) {
                // Baseline promoted to the pending html; re-render will
                // pick it up via srcDoc on the next effect tick.
                baselineHtmlRef.current = null;
                setForceFullReload(n => n + 1);
                return;
              }
              const win = iframeRef.current?.contentWindow;
              if (win) {
                for (const m of patches) {
                  if (m.type === 'xavior-full-reload') continue;
                  win.postMessage({ ...m, n: patchNonceRef.current }, '*');
                }
                baselineHtmlRef.current = pending;
                renderedHtmlRef.current = pending;
              }
            }
          }}
        />
      </div>
    </div>
  );
}
